import React, { useEffect, useMemo, useRef, useState } from 'react';
import '@google/model-viewer';
import { observeAvatarFraming } from './avatarFraming';
import './avatarFraming.css';

/** Local model-viewer wrapper: preserve the canonical assets and motion hooks. */
export function AvatarModel({ avatar, state = 'idle', active = true, reducedMotion = false }) {
  const modelRef = useRef(null);
  const [source, setSource] = useState(avatar.assetUrl);
  const [loadFailed, setLoadFailed] = useState(false);
  const animationName = useMemo(
    () => avatar.animationClips?.[state] || avatar.animationClips?.idle || null,
    [avatar.animationClips, state],
  );
  const resolvedAnimationName = source === avatar.fallbackAssetUrl ? null : animationName;
  // Keep the avatar's viewing angles, but discard the old 90/92% zoom. The
  // automatic initial frame is replaced by an exact bounds fit after load.
  const initialOrbit = useMemo(() => {
    const angles = (avatar.cameraOrbit || '0deg 78deg').trim().split(/\s+/);
    return `${angles[0] || '0deg'} ${angles[1] || '78deg'} 120%`;
  }, [avatar.cameraOrbit]);

  useEffect(() => {
    setSource(avatar.assetUrl);
    setLoadFailed(false);
  }, [avatar.assetUrl]);

  useEffect(() => {
    const model = modelRef.current;
    if (!model || loadFailed) return undefined;
    return observeAvatarFraming(model);
  }, [source, avatar.orientation, avatar.cameraOrbit, avatar.fieldOfView, loadFailed]);

  useEffect(() => {
    const model = modelRef.current;
    if (!model) return;
    const syncAnimation = () => {
      if (!active || reducedMotion || !resolvedAnimationName) model.pause?.();
      else model.play?.({ repetitions: Infinity });
    };
    syncAnimation();
    model.addEventListener('load', syncAnimation);
    return () => model.removeEventListener('load', syncAnimation);
  }, [active, reducedMotion, resolvedAnimationName, source, loadFailed]);

  const handleError = () => {
    if (avatar.fallbackAssetUrl && source !== avatar.fallbackAssetUrl) {
      setSource(avatar.fallbackAssetUrl);
      return;
    }
    setLoadFailed(true);
  };

  if (loadFailed) {
    return (
      <div className="cl-avatar__asset-error" role="img" aria-label={`${avatar.name} model unavailable`}>
        <span>{avatar.name.slice(0, 1)}</span>
        <small>Model unavailable</small>
      </div>
    );
  }

  return (
    <model-viewer
      ref={modelRef}
      className="cl-avatar__model"
      src={source}
      poster={avatar.posterUrl || undefined}
      alt={`${avatar.name}, ${avatar.role}`}
      animation-name={resolvedAnimationName || undefined}
      autoplay={Boolean(active && !reducedMotion && resolvedAnimationName)}
      orientation={avatar.orientation || '0deg 0deg 0deg'}
      camera-orbit={initialOrbit}
      camera-target="auto auto auto"
      field-of-view={avatar.fieldOfView || '28deg'}
      interaction-prompt="none"
      disable-pan
      disable-zoom
      shadow-intensity="0.35"
      shadow-softness="0.9"
      exposure="1"
      loading="lazy"
      reveal="auto"
      onError={handleError}
    />
  );
}

export default AvatarModel;
